import React, { useEffect, useState } from 'react';
import { Check, CheckCircle2, Copy, ExternalLink, Loader2, MessageCircle, Sparkles, X } from 'lucide-react';
import EngagementMedia from '../engagement/EngagementMedia';

const VARIANT_LABELS = {
  playful: 'Playful',
  curious: 'Curious',
  bold: 'Bold perspective',
};

export default function CommentApprovalCard({ item, status, busy, onDecision, onSaveDraft, onMarkPosted, t, selected = false, onSelect, onTextChange }) {
  const [text, setText] = useState(item.sent_text || item.draft || '');
  const [selectedVariantId, setSelectedVariantId] = useState(item.selected_variant_id || item.recommended_variant_id || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [copied, setCopied] = useState(false);
  const post = item.post || {};
  const target = item.target_comment;
  const variants = Array.isArray(item.generated_variants) ? item.generated_variants : [];
  const editable = status === 'pending' && item.status !== 'scheduled';

  useEffect(() => {
    setText(item.sent_text || item.draft || '');
    setSelectedVariantId(item.selected_variant_id || item.recommended_variant_id || null);
  }, [item.draft, item.sent_text, item.selected_variant_id, item.recommended_variant_id]);

  const chooseVariant = async (variant) => {
    if (!editable || savingDraft) return;
    const previousText = text;
    const previousVariant = selectedVariantId;
    setText(variant.text);
    setSelectedVariantId(variant.id);
    onTextChange?.(item.id, variant.text);
    setSavingDraft(true);
    try {
      await onSaveDraft?.(item.id, variant.id, variant.text);
    } catch (_) {
      setText(previousText);
      setSelectedVariantId(previousVariant);
      onTextChange?.(item.id, previousText);
    } finally {
      setSavingDraft(false);
    }
  };

  const saveEditedText = async () => {
    if (!editable || savingDraft || !text.trim() || text === item.draft) return;
    setSavingDraft(true);
    try {
      await onSaveDraft?.(item.id, selectedVariantId, text.trim());
    } catch (_) {
      // The parent already shows a localized error and keeps the local edit visible.
    } finally {
      setSavingDraft(false);
    }
  };

  const copyDraft = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const field = document.createElement('textarea');
        field.value = text;
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        field.remove();
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <article className={`overflow-hidden rounded-2xl border bg-white shadow-[0_18px_40px_-34px_rgba(11,29,58,.45)] ${selected ? 'border-[#12C8C6] ring-2 ring-[#12C8C6]/15' : 'border-[#D7DCE4]'}`}>
      <div className="border-b border-[#E7EAF0] bg-[#F8FAFA] p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {editable && onSelect && <input type="checkbox" checked={selected} onChange={(event) => onSelect(item.id, event.target.checked)} aria-label={t('Select comment')} className="mt-1 h-4 w-4 accent-[#12C8C6]" />}
            <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#667085]">{target ? t('Reply to a post comment') : t('Comment on a post')}</p>
            <h2 className="mt-1 font-semibold text-[#0B1D3A]">{post.author_name || t('LinkedIn member')}</h2>
            </div>
          </div>
          {post.post_url && <a href={post.post_url} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-[#667085] hover:bg-white" aria-label={t('Open post')}><ExternalLink className="h-4 w-4" /></a>}
        </div>
        <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-[#475467]">{post.content || t('No post text available.')}</p>
        <EngagementMedia post={post} t={t} />
      </div>

      <div className="space-y-4 p-4">
        {target && (
          <div className="rounded-xl border-l-4 border-orange-400 bg-orange-50 p-3">
            <p className="flex items-center gap-1.5 text-xs font-bold text-orange-800"><MessageCircle className="h-3.5 w-3.5" />{target.author_name || t('LinkedIn member')}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-[#344054]">{target.content}</p>
          </div>
        )}
        {variants.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0B1D3A]"><Sparkles className="h-3.5 w-3.5 text-[#F79009]" />{t('Draft variants')}</span>
              {savingDraft && <span className="inline-flex items-center gap-1 text-xs text-[#667085]"><Loader2 className="h-3 w-3 animate-spin" />{t('Saving...')}</span>}
            </div>
            <div className="grid gap-2 md:grid-cols-3">
              {variants.map((variant) => {
                const active = selectedVariantId === variant.id;
                const recommended = item.recommended_variant_id === variant.id;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => chooseVariant(variant)}
                    disabled={!editable || savingDraft}
                    className={`rounded-xl border p-3 text-left transition ${active ? 'border-[#12C8C6] bg-[#F0FCFB] ring-2 ring-[#12C8C6]/15' : 'border-[#E4E7EC] bg-white hover:border-[#B8C1CE]'} disabled:cursor-default disabled:opacity-75`}
                  >
                    <span className="flex flex-wrap items-center gap-1.5 text-xs font-bold text-[#0B1D3A]">
                      {t(VARIANT_LABELS[variant.kind] || variant.kind)}
                      {recommended && <span className="rounded-full bg-[#FFF1E6] px-2 py-0.5 text-[10px] font-bold text-[#B54708]">{t('Recommended')}</span>}
                    </span>
                    <span className="mt-2 block whitespace-pre-wrap text-sm leading-5 text-[#475467]">{variant.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <label className="block">
          <span className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0B1D3A]"><Sparkles className="h-3.5 w-3.5 text-[#12C8C6]" />{t('RAG draft')}</span>
          <textarea value={text} onChange={(event) => { setText(event.target.value); onTextChange?.(item.id, event.target.value); }} onBlur={saveEditedText} disabled={!editable} rows={4} maxLength={1250} className="w-full resize-y rounded-xl border border-[#C9D0DA] bg-white p-3 text-sm leading-6 text-[#0B1D3A] outline-none focus:border-[#12C8C6] focus:ring-4 focus:ring-[#12C8C6]/10 disabled:bg-[#F7F8F6]" />
        </label>
        {item.last_error && <p className="rounded-lg bg-red-50 p-2.5 text-sm text-red-700">{item.last_error}</p>}
        {item.status === 'scheduled' && <p className="rounded-lg bg-amber-50 p-2.5 text-sm font-semibold text-amber-800">{t('Scheduled for')} {item.scheduled_for ? new Date(item.scheduled_for).toLocaleString() : t('the next active window')}</p>}
        {editable && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <button onClick={() => onDecision(item.id, 'approve', text)} disabled={busy || !text.trim()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#12C8C6] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t('Approve for manual posting')}
            </button>
            <button onClick={() => onDecision(item.id, 'reject')} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 sm:ml-auto"><X className="h-4 w-4" />{t('Reject')}</button>
          </div>
        )}
        {item.status === 'ready_to_copy' && (
          <div className="grid gap-2 sm:grid-cols-3">
            <button type="button" onClick={copyDraft} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B1D3A] px-4 py-2.5 text-sm font-bold text-white"><Copy className="h-4 w-4" />{t(copied ? 'Copied' : 'Copy comment')}</button>
            {post.post_url ? <a href={post.post_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#C9D0DA] px-4 py-2.5 text-sm font-semibold text-[#0B1D3A]"><ExternalLink className="h-4 w-4" />{t('Open LinkedIn')}</a> : <span className="hidden sm:block" />}
            <button type="button" onClick={() => onMarkPosted?.(item.id)} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#12C8C6] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"><CheckCircle2 className="h-4 w-4" />{t('Mark as posted')}</button>
          </div>
        )}
        {item.status === 'posted' && <p className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" />{t('Marked as manually posted')}</p>}
      </div>
    </article>
  );
}
